import { Color, Spacing } from '@persona-id/design-tokens';
import { PlusIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { Fragment, useCallback, useEffect } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { Controller, FieldErrors, useFieldArray } from 'react-hook-form';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import InternationalOnePanel from 'dashboard/components/DataSourceConfigurationTable/InternationalOnePanel';
import InternationalTwoPlusTwoOnePanel from 'dashboard/components/DataSourceConfigurationTable/InternationalTwoPlusTwoOnePanel';
import {
  RouteIconDrag,
  RouteItem,
} from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/HoverableDraggable';
import { PossibleVendors, VendorConfig } from 'dashboard/models/vendor-config';
import { VerificationTemplateVersionDatabase } from 'dashboard/models/verification-template-version';
import { ISO_3166_COUNTRY_CODES } from 'dashboard/views/DashboardSARShowView/FormSAR/constants';
import Button from 'design-system/components/Button';
import { ErrorText } from 'design-system/components/FormControl';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';

const PANEL_FOOTER_HEIGHT = 64;

const Section = styled.section`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const RouteList = styled.div<{ isDraggingOver: boolean }>`
  ${({ isDraggingOver }) => css`
    background-color: ${isDraggingOver ? COLORS.indigo10 : COLORS.white};
  `};
  transition:
    background-color 0.4s ease-in-out,
    box-shadow 0.4s ease-in-out;
`;

export type DataSourceConfigurationPanelForm = {
  countryCode: string;
  configurations: VendorConfig[];
};

const Form = styled.form`
  flex-direction: column;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const TitleContainer = styled.div`
  padding: ${Spacing[4]};
  padding-bottom: 0;
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_FOOTER_HEIGHT}px);
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  overflow-y: auto;
`;

const ItemContainer = styled.section`
  flex: 1;
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

const FooterContainer = styled.footer`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

const StyledButton = styled(Button)`
  background-color: ${Color.CoreSlate200};
  margin-left: auto;
  width: auto;
`;

const validateDataSourceConfigurationPanelForm = (data: DataSourceConfigurationPanelForm) => {
  const errors: FieldErrors<DataSourceConfigurationPanelForm> = {};

  return Object.keys(errors).length === 0
    ? { values: data, errors: {} }
    : { values: {}, errors: errors };
};
const FieldErrorText = ({ message }: { message?: string }) =>
  message ? (
    <>
      <Spacer height={0.5} />
      <ErrorText>{message}</ErrorText>
    </>
  ) : null;

const AVAILABLE_VENDORS = [
  PossibleVendors.INT1,
  PossibleVendors.INT2,
  PossibleVendors.INT2X21,
  PossibleVendors.INT2X22,
];

interface DataSourceConfigurationPanelProps {
  vtmplVersion: VerificationTemplateVersionDatabase;
  selectedCountry: string | undefined;
  closeConfig: () => void;
  updateConfig: (config: VendorConfig[], countryCode: string) => void;
  deleteConfig: (countryCode: string) => void;
}

const DataSourceConfigurationPanel = ({
  vtmplVersion,
  selectedCountry,
  closeConfig,
  updateConfig,
  deleteConfig,
}: DataSourceConfigurationPanelProps) => {
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        closeConfig();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [closeConfig]);

  const isNew = selectedCountry == null;
  const [isMouseOver, { enable: handleMouseEnter, disable: handleMouseLeave }] = useToggle(false);

  const defaultValues: Partial<DataSourceConfigurationPanelForm> = {
    countryCode: selectedCountry,
    configurations: vtmplVersion.attributes['vendor-configurations'][selectedCountry || ''] || [],
  };

  const formReturn = useFormSecurely<DataSourceConfigurationPanelForm>({
    defaultValues,
    resolver: (data, _) => validateDataSourceConfigurationPanelForm(data),
    mode: 'onBlur',
  });

  // Reset the form when the selected country changes
  useEffect(() => {
    formReturn.reset(defaultValues);
  }, [selectedCountry]);

  const { watch, handleSubmit, control } = formReturn;
  const handleCancel = () => {
    closeConfig();
  };

  const handleSubmitForm = handleSubmit((data) => {
    updateConfig(data.configurations, data.countryCode);
    closeConfig();
  });

  const handleDelete = (countryCode: string) => {
    deleteConfig(countryCode);
    closeConfig();
  };

  const countryCode = watch('countryCode');
  const { fields, append, remove, move } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'configurations', // unique name for your Field Array
  });

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    move(result.source.index, result.destination.index);
  };

  const handleAddVendor = () => {
    append({ vendor: null });
  };
  const handleDeleteVendor = (idx: number) => {
    remove(idx);
  };

  const draggables = fields?.map((item, index) => {
    const errors: FieldErrors<DataSourceConfigurationPanelForm> = {};

    const vendor = watch(`configurations.${index}.vendor`);
    return (
      <Fragment key={item.id}>
        <Draggable draggableId={item.id} index={index} isDragDisabled={false}>
          {(draggableProvided, draggableSnapshot) => {
            let panel;
            switch (vendor) {
              case PossibleVendors.INT1:
                panel = (
                  <InternationalOnePanel
                    formReturn={formReturn}
                    countryCode={countryCode}
                    index={index}
                    draggableProvided={draggableProvided}
                  />
                );
                break;
              case PossibleVendors.INT2X21:
                panel = (
                  <InternationalTwoPlusTwoOnePanel
                    formReturn={formReturn}
                    countryCode={countryCode}
                    index={index}
                    draggableProvided={draggableProvided}
                  />
                );
                break;
            }

            return (
              <RouteItem
                {...draggableProvided.draggableProps}
                {...draggableProvided.dragHandleProps}
                ref={draggableProvided.innerRef}
                isDragDisabled={false}
                isDragging={draggableSnapshot.isDragging}
                style={draggableProvided.draggableProps.style}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <RouteIconDrag $active={isMouseOver} />
                <ItemContainer>
                  <Section>
                    <StyledButton
                      size='sm'
                      width='auto'
                      prefixIcon={<TrashIcon />}
                      onClick={() => handleDeleteVendor(index)}
                    />
                    <StyledLabel>Data Source</StyledLabel>
                    <Controller
                      name={`configurations.${index}.vendor`}
                      control={control}
                      render={({ field }) => (
                        <FieldWrapper>
                          <Select
                            {...field}
                            placeholder='Select data source'
                            hasError={
                              errors?.configurations
                                ? !!errors?.configurations[index]?.vendor
                                : false
                            }
                          >
                            {AVAILABLE_VENDORS.map((vendor) => (
                              <Option key={vendor} value={vendor}>
                                {vendor}
                              </Option>
                            ))}
                          </Select>
                        </FieldWrapper>
                      )}
                    />
                    <FieldErrorText
                      message={
                        errors?.configurations
                          ? errors?.configurations[index]?.vendor?.message
                          : undefined
                      }
                    />
                  </Section>
                  {panel}
                </ItemContainer>
              </RouteItem>
            );
          }}
        </Draggable>
      </Fragment>
    );
  });

  const renderDraggables = useCallback(
    (droppableProvided: DroppableProvided, droppableSnapshot: DroppableStateSnapshot) => (
      <RouteList ref={droppableProvided.innerRef} isDraggingOver={droppableSnapshot.isDraggingOver}>
        {draggables}
        {droppableProvided.placeholder}
      </RouteList>
    ),
    [draggables],
  );

  return (
    <Form id='config-panel__form' onSubmit={handleSubmitForm}>
      <BodyContainer>
        <TitleContainer>
          <Paragraph size='lg' weight='bold' color='primary'>
            {isNew ? 'Add' : 'Edit'} configuration
          </Paragraph>
        </TitleContainer>
        <Section>
          <Label>Country Code</Label>
          <Controller
            name='countryCode'
            control={control}
            render={({ field }) => (
              <FieldWrapper>
                <Select {...field} placeholder='Select country code'>
                  {ISO_3166_COUNTRY_CODES.map((country) => (
                    <Option
                      key={country.const}
                      value={country.const}
                      disabled={Object.keys(
                        vtmplVersion.attributes['vendor-configurations'],
                      ).includes(country.const)}
                    >
                      {country.title}
                    </Option>
                  ))}
                </Select>
              </FieldWrapper>
            )}
          />
          <Spacer height={1.5} />
        </Section>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='droppable'>{renderDraggables}</Droppable>
        </DragDropContext>
        {countryCode && (
          <StyledButton
            size='sm'
            width='auto'
            prefixIcon={<PlusIcon />}
            onClick={handleAddVendor}
            style={{ marginTop: Spacing[3], marginRight: Spacing[6] }}
          />
        )}
      </BodyContainer>
      <FooterContainer>
        <HStack style={{ flexGrow: 1 }}>
          <Button
            id='config-panel__close-button'
            width='auto'
            variant={isNew ? 'basic' : 'danger'}
            onClick={() => (isNew ? handleCancel() : handleDelete(selectedCountry))}
          >
            {isNew ? 'Cancel' : 'Delete'}
          </Button>
          <Button
            id={`config-panel__${isNew ? 'add' : 'update'}-button`}
            type='submit'
            width='auto'
            onClick={handleSubmitForm}
            disabled={fields.length === 0}
          >
            {isNew ? 'Add' : 'Update'}
          </Button>
        </HStack>
      </FooterContainer>
    </Form>
  );
};

export default DataSourceConfigurationPanel;
